import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';

export default function BHELMyLocation() {
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const locationId = user.location;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!locationId) return;
    client.get(`/bhel/location/${locationId}/`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [locationId]);

  if (loading) return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-[#C1278D] border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">Loading your location…</p>
      </div>
    </div>
  );
  if (!data) return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <p className="text-red-500 font-semibold">Location not assigned to your account.</p>
      </div>
    </div>
  );

  const uptimeNum = data.uptime_percent;
  const uptimeColor = uptimeNum >= 99 ? 'text-emerald-600' : uptimeNum >= 95 ? 'text-amber-600' : 'text-red-600';
  const uptimeBg = uptimeNum >= 99 ? 'bg-emerald-500' : uptimeNum >= 95 ? 'bg-amber-500' : 'bg-red-500';
  const uptimeBadgeBg = uptimeNum >= 99 ? 'bg-emerald-100 border-emerald-200 text-emerald-700' : uptimeNum >= 95 ? 'bg-amber-100 border-amber-200 text-amber-700' : 'bg-red-100 border-red-200 text-red-700';
  const pmDone = data.pm_rooms?.filter(r => r.status === 'Closed').length || 0;
  const pmTotal = data.pm_rooms?.length || 0;
  const pmPct = pmTotal > 0 ? Math.round((pmDone / pmTotal) * 100) : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Hero Card */}
      <div className="relative bg-gradient-to-br from-[#3D3562] to-[#272244] rounded-2xl shadow-lg p-6 overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-16 translate-x-16" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#C1278D]/20 rounded-full translate-y-12 -translate-x-8" />
        <div className="relative flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-purple-300 uppercase tracking-widest">My Location</span>
              <span className="px-2 py-0.5 rounded-full bg-white/10 text-purple-200 text-xs font-medium border border-white/10">
                {data.quarter}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-white leading-tight">{data.location.name}</h1>
            <p className="text-purple-300 mt-1 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              {data.location.city}, {data.location.state}
            </p>
          </div>
          <div className="text-right flex-shrink-0 ml-4">
            <div className={`inline-flex flex-col items-center px-5 py-3 rounded-2xl border ${uptimeBadgeBg} bg-opacity-90`}>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80">Uptime</p>
              <p className={`text-3xl font-bold ${uptimeColor}`}>{uptimeNum}%</p>
              <div className="flex items-center gap-1 mt-1">
                <span className={`w-2 h-2 rounded-full ${uptimeBg}`} />
                <span className="text-xs opacity-70">{uptimeNum >= 99 ? 'Excellent' : uptimeNum >= 95 ? 'Moderate' : 'Critical'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Open Tickets */}
        <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100 shadow-sm p-5 overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-amber-100 rounded-full -translate-y-4 translate-x-4 opacity-60" />
          <div className="relative">
            <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center mb-3 shadow-sm">
              <svg className="w-4.5 h-4.5 text-white w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Open Tickets</p>
            <p className={`text-3xl font-bold mt-1 ${data.open_tickets > 0 ? 'text-amber-700' : 'text-gray-300'}`}>{data.open_tickets}</p>
            <p className="text-xs text-amber-500 mt-1">{data.open_tickets > 0 ? 'Requires attention' : 'All clear'}</p>
          </div>
        </div>

        {/* Uptime */}
        <div className={`relative rounded-2xl border shadow-sm p-5 overflow-hidden ${
          uptimeNum >= 99 ? 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100'
          : uptimeNum >= 95 ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-100'
          : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-100'
        }`}>
          <div className={`absolute top-0 right-0 w-16 h-16 rounded-full -translate-y-4 translate-x-4 opacity-60 ${
            uptimeNum >= 99 ? 'bg-emerald-100' : uptimeNum >= 95 ? 'bg-amber-100' : 'bg-red-100'
          }`} />
          <div className="relative">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 shadow-sm ${
              uptimeNum >= 99 ? 'bg-emerald-500' : uptimeNum >= 95 ? 'bg-amber-500' : 'bg-red-500'
            }`}>
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <p className={`text-xs font-semibold uppercase tracking-wider ${
              uptimeNum >= 99 ? 'text-emerald-600' : uptimeNum >= 95 ? 'text-amber-600' : 'text-red-600'
            }`}>Uptime This Quarter</p>
            <p className={`text-3xl font-bold mt-1 ${uptimeColor}`}>{uptimeNum}%</p>
            <p className={`text-xs mt-1 ${uptimeNum >= 99 ? 'text-emerald-500' : uptimeNum >= 95 ? 'text-amber-500' : 'text-red-500'}`}>
              {uptimeNum >= 99 ? 'Target achieved' : uptimeNum >= 95 ? 'Below target' : 'Critical — action needed'}
            </p>
          </div>
        </div>

        {/* RE Attendance */}
        <div className="relative bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl border border-sky-100 shadow-sm p-5 overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-sky-100 rounded-full -translate-y-4 translate-x-4 opacity-60" />
          <div className="relative">
            <div className="w-9 h-9 rounded-xl bg-sky-500 flex items-center justify-center mb-3 shadow-sm">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-xs font-semibold text-sky-600 uppercase tracking-wider">RE Attendance (Month)</p>
            <p className="text-3xl font-bold mt-1 text-sky-700">
              {data.attendance_this_month?.percent != null ? `${data.attendance_this_month.percent}%` : '—'}
            </p>
            <p className="text-xs text-sky-500 mt-1">{data.attendance_this_month?.present} / {data.attendance_this_month?.total} days</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* RE Status Today */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#3D3562] to-[#272244] flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
            </div>
            <h2 className="font-bold text-gray-800">RE Status Today</h2>
          </div>
          <div className="p-5">
            {data.re_today.length === 0 ? (
              <div className="text-center py-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <p className="text-gray-400 text-sm">No attendance marked yet today.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {data.re_today.map((re, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#3D3562] to-[#272244] flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">{re.name.charAt(0)}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-700">{re.name}</span>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${
                      re.present
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${re.present ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      {re.present ? 'Present' : 'Absent'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* PM Rooms */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#3D3562] to-[#272244] flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                </div>
                <h2 className="font-bold text-gray-800">PM Status — Current Half</h2>
              </div>
              <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded-lg border border-purple-100">{pmDone}/{pmTotal}</span>
            </div>
            <div className="mt-2.5">
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#3D3562] to-[#C1278D] rounded-full transition-all" style={{ width: `${pmPct}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{pmPct}% complete</p>
            </div>
          </div>
          <div className="p-5 space-y-2 max-h-52 overflow-y-auto">
            {data.pm_rooms.map((room, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                <span className="text-sm text-gray-700 truncate font-medium">{room.room_name}</span>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  {room.has_report && (
                    <button
                      onClick={() => navigate(`/bhel/pm-report/${room.ticket_id}`)}
                      className="text-xs text-[#C1278D] font-semibold hover:underline"
                    >View</button>
                  )}
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${
                    room.status === 'Closed'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : room.status === 'Not Created'
                      ? 'bg-gray-100 text-gray-500 border-gray-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      room.status === 'Closed' ? 'bg-emerald-500' : room.status === 'Not Created' ? 'bg-gray-400' : 'bg-amber-500'
                    }`} />
                    {room.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
